import type { SchemaField } from "./tool-schema-fields"

export interface FieldPathSuggestion {
	path: string
	type: string
	/** Derived from a container, not a path the tool advertises verbatim. */
	wildcard: boolean
}

/** `items[].*` for a list of objects, `metadata.*` for a plain object. */
function wildcardFor(field: SchemaField, paths: string[]): string | null {
	if (field.kind !== "group") return null
	if (paths.some((path) => path.startsWith(`${field.path}[]`))) {
		return `${field.path}[].*`
	}
	if (paths.some((path) => path.startsWith(`${field.path}.`))) {
		return `${field.path}.*`
	}
	return null
}

function score(path: string, needle: string): number {
	const lower = path.toLowerCase()
	if (lower === needle) return 4
	if (lower.startsWith(needle)) return 3
	const leaf = lower.slice(lower.lastIndexOf(".") + 1)
	if (leaf.startsWith(needle)) return 2
	return lower.includes(needle) ? 1 : 0
}

export function fieldPathSuggestions({
	fields,
	taken,
	query,
	limit = 40,
}: {
	fields: readonly SchemaField[]
	taken: readonly string[]
	query: string
	limit?: number
}): FieldPathSuggestion[] {
	const paths = fields.map((field) => field.path)
	const seen = new Set(taken)
	const rows: FieldPathSuggestion[] = []

	for (const field of fields) {
		if (field.kind === "leaf" && !seen.has(field.path)) {
			seen.add(field.path)
			rows.push({ path: field.path, type: field.type, wildcard: false })
		}
		const wildcard = wildcardFor(field, paths)
		if (wildcard && !seen.has(wildcard)) {
			seen.add(wildcard)
			rows.push({ path: wildcard, type: "any key", wildcard: true })
		}
	}

	const needle = query.trim().toLowerCase()
	if (!needle) return rows.slice(0, limit)

	return rows
		.map((row, index) => ({ row, index, score: score(row.path, needle) }))
		.filter((entry) => entry.score > 0)
		.sort(
			(a, b) =>
				b.score - a.score ||
				// A path the tool actually advertises beats a wildcard we derived.
				Number(a.row.wildcard) - Number(b.row.wildcard) ||
				a.index - b.index,
		)
		.slice(0, limit)
		.map((entry) => entry.row)
}
